import { useEffect, useRef, useState } from "react";
import { Layout, ChevronDown } from "lucide-react";

import { TarefaDTO } from "@/types/tarefa.type";
import { cn } from "@/lib/utils";

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import MarkdownRenderer from "@/components/ui/markdown-reader";

interface TarefaConteudoCardProps {
  tarefa: TarefaDTO;
}

/** Altura (px) a partir da qual a descrição é colapsada com "Ver mais". */
const COLLAPSED_MAX_HEIGHT = 480;

/**
 * Card da descrição da tarefa (somente leitura). Título e descrição são
 * imutáveis após a criação; a visibilidade — único campo editável de uma tarefa
 * genérica — é alterada no card de visibilidade da sidebar.
 *
 * Descrições longas (markdown) são colapsadas a uma altura máxima com um fade e
 * um botão "Ver mais", para não esticar a página. O botão só aparece quando o
 * conteúdo de fato excede o limite.
 */
export function TarefaConteudoCard({ tarefa }: TarefaConteudoCardProps) {
  const contentRef = useRef<HTMLDivElement>(null);
  const [canExpand, setCanExpand] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);

  const hasDescricao = !!tarefa.descricao?.trim();

  // Mede o conteúdo real (scrollHeight ignora o clip) e decide se vale colapsar.
  // ResizeObserver cobre carregamento tardio (imagens, fontes) e resize da janela.
  useEffect(() => {
    const el = contentRef.current;
    if (!el) return;

    const check = () => setCanExpand(el.scrollHeight > COLLAPSED_MAX_HEIGHT + 8);
    check();

    const observer = new ResizeObserver(check);
    observer.observe(el);
    return () => observer.disconnect();
  }, [tarefa.descricao]);

  const collapsed = canExpand && !isExpanded;

  return (
    <Card className="shadow-sm">
      <CardHeader className="flex-row items-center justify-between space-y-0 border-b bg-muted/20 pb-4">
        <CardTitle className="flex items-center gap-2 text-base font-semibold">
          <Layout className="h-4 w-4 text-primary" /> Descrição
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-6">
        <div className="relative">
          <div
            ref={contentRef}
            className={cn(
              "prose prose-sm max-w-full overflow-x-hidden break-words dark:prose-invert",
              collapsed && "overflow-y-hidden",
            )}
            style={collapsed ? { maxHeight: COLLAPSED_MAX_HEIGHT } : undefined}
          >
            {hasDescricao ? (
              <MarkdownRenderer content={tarefa.descricao} />
            ) : (
              <p className="text-sm italic text-muted-foreground">Sem descrição.</p>
            )}
          </div>

          {collapsed && (
            <div className="pointer-events-none absolute inset-x-0 bottom-0 h-20 bg-gradient-to-t from-card to-transparent" />
          )}
        </div>

        {canExpand && (
          <div className="mt-1 flex justify-center">
            <Button
              variant="ghost"
              size="sm"
              className="h-8 gap-1.5 text-muted-foreground hover:text-foreground"
              onClick={() => setIsExpanded((v) => !v)}
            >
              {isExpanded ? "Ver menos" : "Ver mais"}
              <ChevronDown
                className={cn("h-4 w-4 transition-transform", isExpanded && "rotate-180")}
              />
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
